import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getEvents, saveEvent, getSavedEvents } from '../utils/api';
import { useToastContext } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import EventCard from '../components/events/EventCard';
import DiscoverMap from '../components/events/DiscoverMap';
import EmptyState from '../components/common/EmptyState';
import { useTheme } from '../context/ThemeContext';
import useWindowWidth from '../hooks/useWindowWidth';
import { HeartIcon } from '../components/common/InlineIcons';
import { SPACING, BORDER_RADIUS, SHADOWS, TRANSITIONS, TYPOGRAPHY } from '../theme/designTokens';
import '../components/common/CardHoverEffect.css';

// Helper for consistent responsive padding
const responsivePadding = (isMobile, isTablet, verticalPadding = '') =>
  isMobile ? `${verticalPadding || SPACING.sm}px 16px` : isTablet ? `${verticalPadding || SPACING.sm}px 24px` : `${verticalPadding || SPACING.sm}px 40px`;

// Top-level category nav — the sticky horizontal pills row
const CATEGORIES = [
  { id: '',          label: 'All' },
  { id: 'business',  label: 'Business' },
  { id: 'culture',   label: 'Culture' },
  { id: 'music',     label: 'Music' },
  { id: 'nightlife', label: 'Nightlife' },
  { id: 'art',       label: 'Art' },
  { id: 'food',      label: 'Food' },
  { id: 'sports',    label: 'Sports' },
  { id: 'fashion',   label: 'Fashion' },
  { id: 'community', label: 'Community' },
  { id: 'online',    label: 'Online' },
];

const SORT_OPTIONS = [
  { id: 'soon',     label: 'Happening Soon',   apiSort: 'date',     apiDate: '' },
  { id: 'today',    label: 'Happening Today',  apiSort: 'date',     apiDate: 'today' },
  { id: 'trending', label: 'Trending',         apiSort: 'trending', apiDate: '' },
  { id: 'near',     label: 'Near Me',          apiSort: 'distance', apiDate: '', requiresGeo: true },
];

const DATE_RANGE_OPTIONS = [
  { id: '',        label: 'Any Date' },
  { id: 'today',   label: 'Today' },
  { id: 'weekend', label: 'This Weekend' },
  { id: 'week',    label: 'This Week' },
  { id: 'month',   label: 'This Month' },
];

const BROWSE_CITY_LINKS = [
  { slug: 'houston',       name: 'Houston' },
  { slug: 'atlanta',       name: 'Atlanta' },
  { slug: 'new-york',      name: 'New York' },
  { slug: 'toronto',       name: 'Toronto' },
  { slug: 'montreal',      name: 'Montreal' },
  { slug: 'washington-dc', name: 'Washington DC' },
  { slug: 'miami',         name: 'Miami' },
];

export default function EventsPage() {
  const { VB } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { addToast } = useToastContext();
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // Data state
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  // Filters
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [dateRange, setDateRange] = useState(searchParams.get('date') || '');
  const [sortId, setSortId] = useState('soon');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  // What the user is typing. `search` is what we actually query with, and it
  // trails the draft by 300ms so a five-letter word is one request, not five.
  const [searchDraft, setSearchDraft] = useState(searchParams.get('search') || '');
  const [savedOnly, setSavedOnly] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [hasGeo, setHasGeo] = useState(false);
  const [showFilterMenu, setShowFilterMenu] = useState(false);

  const filterMenuRef = useRef(null);
  const LIMIT = 12;

  // Detect geolocation availability for "Near Me" sort
  useEffect(() => {
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      setHasGeo(true);
    }
  }, []);

  // Load saved event IDs for the current user
  useEffect(() => {
    if (!user) { setSavedIds(new Set()); return; }
    getSavedEvents()
      .then(r => setSavedIds(new Set((r.data?.data || []).map(e => e._id))))
      .catch(e => console.warn('[silent catch]', e?.message || e));
  }, [user]);

  const handleSaveToggle = useCallback(async (eventId) => {
    if (!user) return;
    try {
      const r = await saveEvent(eventId);
      setSavedIds(prev => {
        const next = new Set(prev);
        if (r.data.saved) { next.add(eventId); } else { next.delete(eventId); }
        return next;
      });
    } catch (e) {
      // Was `catch {}`. A save that fails should say so rather than looking
      // like a control that does nothing.
      addToast(e?.response?.data?.message || 'Could not save this event. Please try again.', 'error');
    }
  }, [user, addToast]);

  // Resolve sort -> effective API params
  const effectiveSort = useMemo(() => {
    const found = SORT_OPTIONS.find(s => s.id === sortId) || SORT_OPTIONS[0];
    return found;
  }, [sortId]);

  // Effective date (sort can override date for "today")
  const effectiveDate = dateRange || effectiveSort.apiDate || '';

  // Load events from backend
  const loadEvents = useCallback(async (reset = false) => {
    if (reset) { setLoading(true); setPage(1); setHasMore(true); }
    else setLoadingMore(true);
    try {
      const p = { limit: LIMIT, page: reset ? 1 : page };
      if (searchParams.get('city')) p.city = searchParams.get('city');
      if (category) p.category = category;
      if (effectiveDate) p.date = effectiveDate;
      if (search) p.search = search;
      if (effectiveSort.apiSort && effectiveSort.apiSort !== 'date') p.sort = effectiveSort.apiSort;
      const r = await getEvents(p);
      if (!r.data || typeof r.data !== 'object') throw new Error('Invalid response from server');
      const fetched = r.data.events || [];
      const tot     = r.data.total  || 0;
      if (reset) setEvents(fetched);
      else       setEvents(prev => [...prev, ...fetched]);
      setTotal(tot);
      setHasMore((reset ? LIMIT : page * LIMIT) < tot);
    } catch {}
    finally { setLoading(false); setLoadingMore(false); }
  }, [category, effectiveDate, search, page, effectiveSort.apiSort, searchParams]);

  // Reset + reload when filters change
  useEffect(() => { loadEvents(true); }, [category, effectiveDate, search, sortId, searchParams]);

  // Load more when page increments
  useEffect(() => { if (page > 1) loadEvents(false); }, [page]);

  // Scroll tracking — back-to-top + infinite scroll trigger
  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 600);
      // Infinite scroll: load more when near page bottom
      if (
        hasMore && !loadingMore && !loading &&
        (window.innerHeight + window.scrollY) >= document.body.offsetHeight - 800
      ) {
        setPage(p => p + 1);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [hasMore, loadingMore, loading]);

  // Close menus on outside click
  useEffect(() => {
    const handler = (e) => {
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) setShowFilterMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchDraft.trim()), 300);
    return () => clearTimeout(t);
  }, [searchDraft]);

  const clearFilters = () => {
    setCategory('');
    setDateRange('');
    setSortId('soon');
    setSearch('');
    setSearchDraft('');
    setSavedOnly(false);
  };

  // Display events — respects savedOnly toggle
  const displayEvents = useMemo(() => {
    if (!savedOnly) return events;
    return events.filter(ev => savedIds.has(ev._id));
  }, [events, savedOnly, savedIds]);

  // Event count badge text
  const countLabel = useMemo(() => {
    if (loading) return 'Loading events…';
    const n = savedOnly ? displayEvents.length : total;
    const dateLabel = (DATE_RANGE_OPTIONS.find(d => d.id === effectiveDate) || {}).label;
    let timeSuffix = 'matching your filters';
    if (effectiveDate === 'today') timeSuffix = 'happening today';
    else if (effectiveDate === 'week') timeSuffix = 'happening this week';
    else if (effectiveDate === 'weekend') timeSuffix = 'this weekend';
    else if (effectiveDate === 'month') timeSuffix = 'this month';
    else if (!effectiveDate && !category && !search) timeSuffix = 'available now';
    if (savedOnly) timeSuffix = 'saved' + (dateLabel ? ` · ${dateLabel}` : '');
    return `${n} ${n === 1 ? 'event' : 'events'} ${timeSuffix}`;
  }, [loading, total, savedOnly, displayEvents.length, effectiveDate, category, search]);

  // Active filter count
  const activeFilterCount = [category, dateRange, search, savedOnly ? 'saved' : '', sortId !== 'soon' ? sortId : ''].filter(Boolean).length;

  return (
    <div style={{ background: 'transparent', minHeight: '100vh', position: 'relative' }}>
      <div aria-hidden="true" style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 560, background: 'radial-gradient(64% 100% at 88% 0%, rgba(217,168,59,0.20), transparent 62%), radial-gradient(46% 90% at -4% 4%, rgba(230,57,70,0.08), transparent 60%)', pointerEvents: 'none', zIndex: 0 }} />
      <Helmet>
        <title>Events — Vibana</title>
        <meta name="description" content="Browse African cultural events happening near you. Filter by category, date, and city." />
        <link rel="canonical" href="https://vibana.app/events" />
        <meta property="og:title" content="Events — Vibana" />
        <meta property="og:description" content="Browse African cultural events happening near you. Filter by category, date, and city." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://vibana.app/events" />
        <meta property="og:image" content="https://vibana.app/og-image.jpg" />
      </Helmet>

      <style>{`
        .vb-cat-pills::-webkit-scrollbar { display: none; }
        .vb-events-card { transition: transform ${TRANSITIONS.fast}; }
        /* Chromium draws its own clear X on type="search"; we render a styled
           one, and two of them side by side looks broken. Keep the input type
           for the semantics and the search key on mobile keyboards. */
        .vb-search-input::-webkit-search-cancel-button,
        .vb-search-input::-webkit-search-decoration { -webkit-appearance: none; appearance: none; display: none; }
      `}</style>

      {/* ---- STICKY CATEGORY NAV ---- */}
      <div style={{
        position: 'sticky', top: 'var(--nav-height)', zIndex: 30,
        background: 'rgba(12,11,10,0.82)',
        backdropFilter: 'saturate(1.4) blur(16px)',
        WebkitBackdropFilter: 'saturate(1.4) blur(16px)',
        borderBottom: `1px solid ${VB.borderDark}`,
      }}>
        <div
          className="vb-cat-pills"
          style={{
            display: 'flex',
            gap: SPACING.xs,
            padding: isMobile ? `${SPACING.sm}px 16px` : isTablet ? `${SPACING.sm}px 24px` : `${SPACING.sm}px 40px`,
            overflowX: 'auto',
            scrollbarWidth: 'none',
            scrollSnapType: 'x mandatory',
          }}
        >
          {CATEGORIES.map(cat => {
            const active = category === cat.id;
            return (
              <button
                key={cat.id || '_all'}
                onClick={() => setCategory(cat.id)}
                aria-pressed={active}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 16px',
                  height: 36,
                  borderRadius: BORDER_RADIUS.full,
                  border: active ? 'none' : `1px solid ${VB.borderDark}`,
                  background: active ? VB.red : 'transparent',
                  color: active ? '#FFFFFF' : VB.snowPrimary,
                  fontSize: 13,
                  fontWeight: active ? 700 : 600,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  scrollSnapAlign: 'start',
                  transition: `background ${TRANSITIONS.fast}, color ${TRANSITIONS.fast}, transform ${TRANSITIONS.fast}`,
                  fontFamily: 'var(--font-body)',
                }}
                onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = VB.red; }}
                onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = VB.borderDark; }}
              >
                {cat.label}
              </button>
            );
          })}
        </div>

        {/* ---- FILTER TOOLBAR (single Filters button) ---- */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: SPACING.sm,
          padding: isMobile ? `${SPACING.sm}px 16px ${SPACING.sm}px` : isTablet ? `${SPACING.sm}px 24px ${SPACING.sm}px` : `${SPACING.sm}px 40px ${SPACING.sm}px`,
          borderTop: `0.5px solid ${VB.borderDark}`,
        }}>
          {/* Search by event name. The `search` state and its API wiring already
              existed, and the results header even rendered 'for "<query>"' — but
              nothing on the page could set it, so it was reachable only by typing
              ?search= into the URL. This is the missing input. */}
          <div style={{
            position: 'relative', display: 'flex', alignItems: 'center',
            flex: '1 1 220px', minWidth: 0, maxWidth: 420, height: 36,
            borderRadius: BORDER_RADIUS.compact,
            border: `1px solid ${search ? VB.goldBorder : VB.borderDark}`,
            background: 'transparent',
            transition: `border-color ${TRANSITIONS.fast}`,
          }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke={VB.snowMuted}
              strokeWidth="2" style={{ flexShrink: 0, marginLeft: 12 }} aria-hidden="true">
              <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="21" y2="21" />
            </svg>
            <input
              type="search"
              className="vb-search-input"
              value={searchDraft}
              onChange={e => setSearchDraft(e.target.value)}
              placeholder="Search events by name"
              aria-label="Search events by name"
              style={{
                flex: 1, minWidth: 0, height: '100%',
                padding: '0 10px', border: 'none', outline: 'none',
                background: 'transparent', color: VB.snowPrimary,
                fontSize: 13, fontFamily: 'var(--font-body)',
              }}
            />
            {searchDraft && (
              <button
                onClick={() => { setSearchDraft(''); setSearch(''); }}
                aria-label="Clear search"
                style={{
                  flexShrink: 0, marginRight: 8, padding: 4, lineHeight: 0,
                  background: 'none', border: 'none', cursor: 'pointer', color: VB.snowMuted,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <line x1="6" y1="6" x2="18" y2="18" /><line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              </button>
            )}
          </div>

          <div ref={filterMenuRef} style={{ position: 'relative' }}>
            <button
              onClick={() => setShowFilterMenu(v => !v)}
              aria-haspopup="true"
              aria-expanded={showFilterMenu}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                height: 36, padding: '0 16px',
                borderRadius: BORDER_RADIUS.compact,
                border: `1px solid ${activeFilterCount > 0 ? VB.goldBorder : VB.borderDark}`,
                background: activeFilterCount > 0 ? 'rgba(233,162,59,0.10)' : 'transparent',
                color: activeFilterCount > 0 ? VB.gold : VB.snowPrimary,
                fontSize: 13, fontWeight: 700, cursor: 'pointer',
                fontFamily: 'var(--font-body)',
                transition: `border-color ${TRANSITIONS.fast}, background ${TRANSITIONS.fast}`,
              }}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="4" y1="6" x2="20" y2="6" /><line x1="7" y1="12" x2="17" y2="12" /><line x1="10" y1="18" x2="14" y2="18" />
              </svg>
              <span>Filters</span>
              {activeFilterCount > 0 && (
                <span style={{ background: VB.gold, color: '#1a1613', fontSize: 10, fontWeight: 800, minWidth: 16, height: 16, borderRadius: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{activeFilterCount}</span>
              )}
              <span style={{ color: VB.snowMuted, fontSize: 10 }}>▾</span>
            </button>
            {showFilterMenu && (
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', left: 0,
                background: VB.darkCard, borderRadius: BORDER_RADIUS.standard,
                border: `1px solid ${VB.borderDark}`, boxShadow: SHADOWS.elevated,
                zIndex: 100, width: 264, overflow: 'hidden', padding: '6px 0',
              }}>
                <div style={{ padding: '8px 16px 5px', fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: VB.gold }}>Sort by</div>
                {SORT_OPTIONS.map(opt => {
                  const disabled = opt.requiresGeo && !hasGeo;
                  const selected = sortId === opt.id;
                  return (
                    <button
                      key={opt.id}
                      disabled={disabled}
                      onClick={() => { setSortId(opt.id); }}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        width: '100%', padding: '9px 16px', background: selected ? 'rgba(233,162,59,0.08)' : 'transparent',
                        border: 'none', color: disabled ? VB.snowMuted : VB.snowPrimary,
                        fontSize: 13, fontWeight: selected ? 700 : 500, textAlign: 'left',
                        cursor: disabled ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)',
                        opacity: disabled ? 0.5 : 1,
                      }}
                    >
                      <span>{opt.label}</span>
                      {selected && <span style={{ color: VB.gold, fontSize: 14 }}>✓</span>}
                      {disabled && <span style={{ fontSize: 10, color: VB.snowMuted }}>geo off</span>}
                    </button>
                  );
                })}
                <div style={{ height: 1, background: VB.borderDark, margin: '6px 0' }} />
                <div style={{ padding: '3px 16px 5px', fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: VB.gold }}>Date</div>
                {DATE_RANGE_OPTIONS.map(opt => {
                  const selected = dateRange === opt.id;
                  return (
                    <button
                      key={opt.id || '_any'}
                      onClick={() => { setDateRange(opt.id); }}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        width: '100%', padding: '9px 16px', background: selected ? 'rgba(233,162,59,0.08)' : 'transparent',
                        border: 'none', color: VB.snowPrimary,
                        fontSize: 13, fontWeight: selected ? 700 : 500, textAlign: 'left',
                        cursor: 'pointer', fontFamily: 'var(--font-body)',
                      }}
                    >
                      <span>{opt.label}</span>
                      {selected && <span style={{ color: VB.gold, fontSize: 14 }}>✓</span>}
                    </button>
                  );
                })}
                <div style={{ height: 1, background: VB.borderDark, margin: '6px 0' }} />
                <button
                  onClick={() => { if (!user) { navigate('/login'); return; } setSavedOnly(v => !v); }}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', padding: '10px 16px', background: savedOnly ? 'rgba(233,162,59,0.08)' : 'transparent',
                    border: 'none', color: VB.snowPrimary, fontSize: 13, fontWeight: savedOnly ? 700 : 500,
                    textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font-body)',
                  }}
                >
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><HeartIcon size={14} strokeWidth={2.2} /> Saved events only</span>
                  {savedOnly && <span style={{ color: VB.gold, fontSize: 14 }}>✓</span>}
                </button>
              </div>
            )}
          </div>

          {activeFilterCount > 0 && (
            <button
              onClick={clearFilters}
              style={{
                marginLeft: 'auto',
                background: 'none', border: 'none',
                color: VB.red, fontSize: 13, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'var(--font-body)', padding: '0 6px',
              }}
            >
              Clear all ({activeFilterCount})
            </button>
          )}
        </div>
      </div>

      {/* ---- CONTENT ---- */}
      <div style={{ padding: isMobile ? `${SPACING.lg}px 16px 64px` : isTablet ? `${SPACING.xxxl}px 24px 64px` : `${SPACING.xxxl}px 40px 64px` }}>

        {/* Event count badge */}
        <div style={{
          marginBottom: SPACING.lg,
          display: 'flex',
          alignItems: 'center',
          gap: SPACING.sm,
          flexWrap: 'wrap',
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            ...TYPOGRAPHY.labelSmall,
            color: VB.snowPrimary,
            background: VB.darkCard,
            border: `1px solid ${VB.borderDark}`,
            borderRadius: BORDER_RADIUS.full,
            padding: '5px 12px',
            fontFamily: 'var(--font-body)',
          }}>
            {!loading && (
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: VB.red, display: 'inline-block' }} />
            )}
            {countLabel}
          </span>
          {search && (
            <span style={{ fontSize: 13, color: VB.snowMuted }}>
              for <span style={{ color: VB.snowPrimary, fontWeight: 700 }}>"{search}"</span>
            </span>
          )}
        </div>

        {/* Grid / Map view toggle */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: SPACING.md }}>
          <div style={{ display: 'inline-flex', gap: 2, background: 'rgba(251,244,230,0.05)', border: `1px solid ${VB.borderDark}`, borderRadius: 999, padding: 3 }}>
            {['grid', 'map'].map(m => (
              <button key={m} type="button" onClick={() => setViewMode(m)} aria-pressed={viewMode === m}
                style={{ padding: '7px 16px', borderRadius: 999, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, textTransform: 'capitalize', minHeight: 44,
                  background: viewMode === m ? VB.ivory : 'transparent', color: viewMode === m ? '#151210' : VB.snowMuted }}>
                {m}
              </button>
            ))}
          </div>
        </div>

        {/* Loading skeleton */}
        {loading ? (
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: isMobile ? SPACING.md : SPACING.xl,
          }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} style={{ borderRadius: BORDER_RADIUS.large, overflow: 'hidden', background: VB.darkCard, border: `0.5px solid ${VB.borderDark}` }}>
                <div style={{ height: 200 }} className="vb-skeleton" />
                <div style={{ padding: SPACING.lg }}>
                  {[40, 80, 60].map((w, j) => (
                    <div key={j} className="vb-skeleton" style={{ height: j === 1 ? 18 : 13, width: `${w}%`, borderRadius: 4, marginBottom: SPACING.sm }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : displayEvents.length === 0 ? (
          // Empty state — uses the shared EmptyState component
          <EmptyState
            icon="○"
            headline={savedOnly ? 'No saved events match these filters' : 'No events match your filters'}
            description={savedOnly
              ? 'Try removing the saved-only filter, or save some events first.'
              : 'Try widening your date range or picking a different category.'}
            ctaLabel="Clear filters"
            ctaOnClick={clearFilters}
            theme={VB}
          />
        ) : viewMode === 'map' ? (
          <DiscoverMap
            events={displayEvents}
            theme={VB}
            isMobile={isMobile}
            onEventClick={(ev) => navigate('/events/' + (ev.slug || ev._id))}
          />
        ) : (
          // Events grid — 3-col desktop, 2-col tablet, 1-col mobile
          <>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: isMobile ? SPACING.md : SPACING.xl,
            }}>
              {displayEvents.map(ev => (
                <div key={ev._id} className="vb-events-card" style={{ position: 'relative' }}>
                  <EventCard event={ev} isSaved={savedIds.has(ev._id)} onSaveToggle={user ? handleSaveToggle : null} />
                  {!user && (
                    <button
                      type="button"
                      title="Sign in to save events"
                      aria-label="Sign in to save events"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate('/login'); }}
                      style={{
                        position: 'absolute', top: 10, right: 10,
                        width: 34, height: 34, borderRadius: '50%',
                        border: `1px solid ${VB.cardBorderMid}`,
                        background: 'rgba(8,8,8,0.45)',
                        color: VB.lightSecondary,
                        cursor: 'pointer', zIndex: 12,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                      }}
                    >
                      <HeartIcon size={14} strokeWidth={2} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Loading more spinner */}
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: SPACING.xl, color: VB.snowMuted, fontSize: 14 }}>
                Loading more events…
              </div>
            )}

            {/* Manual load more fallback */}
            {hasMore && !loadingMore && events.length >= LIMIT && (
              <div style={{ textAlign: 'center', marginTop: SPACING.xl }}>
                <button
                  onClick={() => setPage(p => p + 1)}
                  style={{
                    padding: '12px 32px',
                    borderRadius: BORDER_RADIUS.full,
                    border: `1.5px solid ${VB.borderDark}`,
                    background: 'transparent',
                    color: VB.snowMuted,
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: 'var(--font-body)',
                    transition: `border-color ${TRANSITIONS.fast}, color ${TRANSITIONS.fast}`,
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = VB.red; e.currentTarget.style.color = VB.red; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = VB.borderDark; e.currentTarget.style.color = VB.snowMuted; }}
                >
                  Load more events
                </button>
              </div>
            )}

            {!hasMore && events.length > 0 && (
              <div style={{ textAlign: 'center', marginTop: SPACING.xxxl, color: VB.snowMuted, fontSize: 13 }}>
                You've reached the end — {total} events total
              </div>
            )}
          </>
        )}

        {/* Browse by city footer */}
        <div style={{ marginTop: 56, paddingTop: SPACING.xxl, borderTop: `1px solid ${VB.borderDark}` }}>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: VB.snowPrimary, fontFamily: 'var(--font-display)', letterSpacing: '-0.5px' }}>Browse by city</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: SPACING.xs, marginTop: SPACING.sm }}>
            {BROWSE_CITY_LINKS.map((cityLink) => (
              <Link
                key={cityLink.slug}
                to={`/events/${cityLink.slug}`}
                style={{
                  padding: '7px 14px',
                  borderRadius: 8,
                  border: `1px solid ${VB.borderDark}`,
                  color: VB.snowMuted,
                  textDecoration: 'none',
                  fontSize: 13,
                  fontWeight: 600,
                  transition: `border-color ${TRANSITIONS.fast}, color ${TRANSITIONS.fast}`,
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.30)'; e.currentTarget.style.color = VB.snowPrimary; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = VB.borderDark; e.currentTarget.style.color = VB.snowMuted; }}
              >
                {cityLink.name}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ---- BACK TO TOP ---- */}
      {showBackToTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Back to top"
          style={{
            position: 'fixed', bottom: 28, right: 24,
            width: 44, height: 44, borderRadius: 22,
            background: VB.darkCard, color: VB.snowPrimary, border: `1px solid ${VB.borderLight}`,
            cursor: 'pointer', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 50,
            transition: `transform ${TRANSITIONS.fast}, border-color ${TRANSITIONS.fast}`,
          }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.30)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'none'; e.currentTarget.style.borderColor = VB.borderLight; }}
        >
          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 15l7-7 7 7"/>
          </svg>
        </button>
      )}
    </div>
  );
}
